import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import {
  fetchDashboardSummary,
  fetchRecentOrders,
  fetchTopCaterers,
} from '../../lib/adminApi';

const statusBadgeStyles = {
  completed: 'bg-emerald-100 text-emerald-700',
  confirmed: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-sky-100 text-sky-700',
  ready_for_delivery: 'bg-indigo-100 text-indigo-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-rose-100 text-rose-700',
};

function metricCard(icon, label, value, delta) {
  return (
    <div key={label} className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="mt-3 text-3xl font-semibold text-foreground">{value}</p>
          {delta != null && (
            <p className={`mt-2 text-xs font-medium ${delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {delta >= 0 ? '+' : ''}{delta}% vs last week
            </p>
          )}
        </div>
        <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon name={icon} size={22} />
        </span>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [topCaterers, setTopCaterers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const [summaryData, recentOrders, catererLeaders] = await Promise.all([
          fetchDashboardSummary(),
          fetchRecentOrders(8),
          fetchTopCaterers(5),
        ]);

        if (!isMounted) return;

        setSummary(summaryData);
        setOrders(recentOrders);
        setTopCaterers(catererLeaders);
      } catch (err) {
        console.error('Failed to load dashboard data', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const metrics = useMemo(() => {
    if (!summary) {
      return [];
    }

    return [
      metricCard('Store', 'Total Caterers', summary.totalCaterers.toLocaleString()),
      metricCard('UserPlus', 'Unverified Caterers', summary.pendingCaterers.toLocaleString()),
      metricCard('ShieldCheck', 'Elite Caterers', summary.suspendedCaterers.toLocaleString()),
      metricCard('Users', 'Customers', summary.totalCustomers.toLocaleString()),
      metricCard('ClipboardList', 'Orders', summary.totalOrders.toLocaleString()),
      metricCard('Timer', 'Orders In Flight', summary.inProgressOrders.toLocaleString()),
      metricCard('Wallet', '12-Month Platform Revenue', `R${summary.trailingRevenue.toLocaleString(undefined, { minimumFractionDigits: 0 })}`),
    ];
  }, [summary]);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">Marketplace Pulse</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Live snapshot of caterer supply, order health, and platform revenue across Mrs Cater.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {loading && metrics.length === 0
          ? Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="animate-pulse rounded-xl border border-border bg-card p-6">
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="mt-4 h-8 w-20 rounded bg-muted" />
                <div className="mt-3 h-3 w-16 rounded bg-muted" />
              </div>
            ))
          : metrics}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b border-border/80 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Recent Orders</h3>
                <p className="text-xs text-muted-foreground">Newest bookings across both customer and caterer channels.</p>
              </div>
              <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                {orders.length.toString().padStart(2, '0')} orders
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead className="bg-muted/30 text-muted-foreground">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold">Order</th>
                    <th className="px-6 py-3 text-left font-semibold">Customer</th>
                    <th className="px-6 py-3 text-left font-semibold">Caterer</th>
                    <th className="px-6 py-3 text-left font-semibold">Guests</th>
                    <th className="px-6 py-3 text-left font-semibold">Amount</th>
                    <th className="px-6 py-3 text-left font-semibold">Status</th>
                    <th className="px-6 py-3 text-right font-semibold">Placed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/80">
                  {loading && orders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-6 text-center text-muted-foreground">
                        Loading orders…
                      </td>
                    </tr>
                  )}

                  {!loading && orders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-6 py-6 text-center text-muted-foreground">
                        No recent orders found.
                      </td>
                    </tr>
                  )}

                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-muted/40">
                      <td className="px-6 py-3 font-medium text-foreground">{order.id}</td>
                      <td className="px-6 py-3 text-muted-foreground">{order.customer?.full_name ?? order.customer?.email ?? '—'}</td>
                      <td className="px-6 py-3 text-muted-foreground">{order.caterer?.name ?? '—'}</td>
                      <td className="px-6 py-3 text-foreground">{order.guests ?? '—'}</td>
                      <td className="px-6 py-3 font-semibold text-foreground">
                        {order.total_amount ? `R${Number(order.total_amount).toLocaleString()}` : '—'}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          statusBadgeStyles[order.status] ?? 'bg-muted text-foreground'
                        }`}>
                          {order.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-right text-muted-foreground">
                        {order.created_at ? format(new Date(order.created_at), 'dd MMM yyyy') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <div className="rounded-xl border border-border bg-card shadow-sm">
            <div className="border-b border-border/80 px-6 py-4">
              <h3 className="text-lg font-semibold text-foreground">Top Caterers</h3>
              <p className="text-xs text-muted-foreground">Ranked by lifetime revenue.</p>
            </div>
            <ul className="divide-y divide-border/80">
              {loading && topCaterers.length === 0 ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <li key={idx} className="flex items-center justify-between px-6 py-4">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                    <div className="h-4 w-16 animate-pulse rounded bg-muted" />
                  </li>
                ))
              ) : topCaterers.length === 0 ? (
                <li className="px-6 py-6 text-center text-sm text-muted-foreground">No caterer performance data yet.</li>
              ) : (
                topCaterers.map((caterer) => (
                  <li key={caterer.id} className="flex items-center justify-between px-6 py-4">
                    <div>
                      <p className="text-sm font-semibold text-foreground">{caterer.name}</p>
                      <p className="text-xs text-muted-foreground">{caterer.status?.toUpperCase() ?? 'ACTIVE'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-foreground">R{Number(caterer.total_revenue ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{caterer.total_orders ?? 0} orders</p>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-foreground">Operational Alerts</h3>
            <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="mt-0.5 text-amber-500" />
                <span>Set up automated escalation rules for overdue escrow releases.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="Clock" size={16} className="mt-0.5 text-sky-500" />
                <span>Monitor caterers approaching onboarding expiry.</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="TrendingDown" size={16} className="mt-0.5 text-rose-500" />
                <span>Flag drop in weekly bookings vs target baseline.</span>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}
